import './App.css';

function App() {
  return (
    <div className="App">
      
  <h2>1. HTML (HyperText Markup Language)</h2>
  <p>📌 Learn how to structure a web page using HTML elements.</p>
  <h3>Key Topics:</h3>
  <p><strong>Basic Structure</strong></p>
  <p>&lt;!DOCTYPE html&gt;, &lt;html&gt;, &lt;head&gt;, &lt;body&gt;</p>

  <p><strong>Text &amp; Formatting Tags</strong></p>
  <p>&lt;h1&gt; to &lt;h6&gt;, &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;strong&gt;, &lt;em&gt;</p>

  <p><strong>Lists</strong></p>
  <p>Ordered: &lt;ol&gt;, Unordered: &lt;ul&gt;, &lt;li&gt;</p>

  <p><strong>Links and Anchors</strong></p>
  <p>&lt;a href="..."&gt;</p>

  <p><strong>Images and Media</strong></p>
  <p>&lt;img&gt;, &lt;video&gt;, &lt;audio&gt;</p>

  <p><strong>Forms and Input Elements</strong></p>
  <p>&lt;form&gt;, &lt;input&gt;, &lt;textarea&gt;, &lt;select&gt;, &lt;button&gt;</p>

  <p><strong>Tables</strong></p>
  <p>&lt;table&gt;, &lt;thead&gt;, &lt;tbody&gt;, &lt;tr&gt;, &lt;td&gt;, &lt;th&gt;</p>

  <p><strong>Semantic Tags</strong></p>
  <p>&lt;header&gt;, &lt;nav&gt;, &lt;section&gt;, &lt;article&gt;, &lt;footer&gt;</p>

  <p><strong>Meta tags &amp; SEO Basics</strong></p>
  <p>&lt;meta charset&gt;, &lt;meta name="description"&gt;</p>

  <p>✅ You should be able to build a complete static webpage with forms and images.</p>

  <h2>2. CSS (Cascading Style Sheets)</h2>
  <p>📌 Style and layout your HTML content.</p>
  <h3>Key Topics:</h3>

  <p><strong>Syntax &amp; Selectors</strong></p>
  <p>Tag, class (.class), ID (#id), attribute selectors</p>

  <p><strong>Colors, Fonts, Text</strong></p>
  <p>color, font-family, font-size, text-align, line-height</p>

  <p><strong>Box Model</strong></p>
  <p>margin, padding, border, width, height</p>

  <p><strong>Positioning &amp; Layout</strong></p>
  <p>position (static, relative, absolute, fixed, sticky)</p>
  <p>display (block, inline, inline-block, flex, grid)</p>
  <p>z-index</p>

  <p><strong>Flexbox</strong></p>
  <p>display: flex, justify-content, align-items, flex-wrap</p>

  <p><strong>Grid</strong></p>
  <p>display: grid, grid-template-columns, grid-gap</p>

  <p><strong>Responsive Design</strong></p>
  <p>Media queries: @media, breakpoints</p>

  <p><strong>Pseudo-classes and Pseudo-elements</strong></p>
  <p>:hover, :active, ::before, ::after</p>

  <p><strong>CSS Transitions &amp; Animations</strong></p>
  <p>transition, animation, @keyframes</p>

  <p>✅ Be able to create a responsive, styled layout with Flexbox and media queries.</p>

  <h2>3. JavaScript (Most Important)</h2>
  <p>📌 Core logic behind React. JS is the engine powering your React components.</p>

  <h3>3.1. JavaScript Basics</h3>
  <p>Variables (let, const, var)</p>
  <p>Data Types (string, number, boolean, null, undefined, object, array)</p>
  <p>Operators (+, -, ==, ===, &amp;&amp;, ||, ternary ? :)</p>
  <p>Type Conversion (implicit and explicit)</p>
  <p>Template Literals: <code>Hello "$name"</code></p>

  <h3>3.2. Control Structures</h3>
  <p>if, else, else if</p>
  <p>switch</p>
  <p>Loops: for, while, do...while, for...in, for...of</p>

  <h3>3.3. Functions</h3>
  <p>Function declarations and expressions</p>
  <p>Arrow functions: <code>() =&gt; {}</code></p>
  <p>Default parameters</p>
  <p>Higher-order functions (functions returning or taking other functions)</p>

  <h3>3.4. Arrays</h3>
  <p>Declaration and access</p>
  <p>Common methods: push, pop, shift, unshift, slice, splice</p>
  <p>Array methods (used heavily in React):</p>
  <p>map, filter, reduce, forEach, find, some, every, sort</p>

  <h3>3.5. Objects</h3>
  <p>Creation and access (obj.key / obj['key'])</p>
  <p>Destructuring: const &#123; name &#125; = user;</p>
  <p>Object methods and this keyword</p>
  <p>Spread/rest operator: &#123;...obj&#125;, [...arr], ...args</p>

  <h3>3.6. DOM Manipulation</h3>
  <p>document.querySelector, getElementById</p>
  <p>addEventListener</p>
  <p>createElement, appendChild, removeChild</p>
  <p>Event handling (onClick, onInput)</p>
  <p>(React hides most manual DOM handling, but understanding it is still useful)</p>

  <h3>3.7. ES6+ (Modern JavaScript Features)</h3>
  <p>Arrow functions</p>
  <p>let / const</p>
  <p>Template literals</p>
  <p>Destructuring (arrays &amp; objects)</p>
  <p>Spread and Rest ...</p>
  <p>Modules (import / export)</p>
  <p>Classes &amp; this keyword</p>
  <p>Optional chaining (obj?.prop)</p>
  <p>Nullish coalescing (??)</p>

  <h3>3.8. Functions vs Classes</h3>
  <p>Understand constructor functions</p>
  <p>Difference between class and function</p>
  <p>Why React moved from class components to functional components with Hooks</p>

  <h3>3.9. Closures, Scope, Hoisting</h3>
  <p>Local vs Global scope</p>
  <p>Lexical scope</p>
  <p>Closure behavior</p>
  <p>Function hoisting vs variable hoisting</p>

  <h2>4. JavaScript Asynchronous Concepts</h2>
  <p>📌 Very important for fetching data in React apps.</p>
  <p>Callback functions</p>
  <p>Promises</p>
  <p>.then(), .catch(), .finally()</p>
  <p>Async/Await</p>
  <p>Fetch API</p>
  <p>fetch(url).then(res =&gt; res.json())</p>
  <p>Error Handling</p>
  <p>Try/catch blocks</p>
  <p>✅ You should be able to fetch data from an API and handle the result.</p>

  <h2>5. Basic Git &amp; GitHub (Optional but highly recommended)</h2>
  <p>📌 To manage version control and projects.</p>
  <p>git init, git status, git add, git commit</p>
  <p>Branches: git checkout -b feature-xyz</p>
  <p>GitHub: Push/pull repos, create PRs</p>

  <h2>6. Basic NPM (Node Package Manager)</h2>
  <p>📌 React uses packages from NPM for routing, state, UI libraries, etc.</p>
  <p>What is NPM</p>
  <p>npm init, npm install package-name</p>
  <p>package.json and node_modules</p>
  <p>Scripts: npm start, npm run build</p>

  <h2>7. Basic Webpack/Bundling Concepts (Nice to know)</h2>
  <p>What is bundling?</p>
  <p>What are modules?</p>
  <p>Role of webpack, babel, and transpiling</p>
  <p>✅ You don’t need to write Webpack config, but understand what it does behind the scenes.</p>

  <h2>8. Understanding How the Web Works</h2>
  <p>📌 Important for working with client-server architecture in React.</p>
  <p>What is a browser, how it renders HTML/CSS/JS</p>
  <p>What is the DOM</p>
  <p>What is the Client-Server model</p>
  <p>HTTP Methods: GET, POST, PUT, DELETE</p>
  <p>APIs and JSON</p>
  <p>Status codes: 200, 404, 500, etc.</p>
</div>

    
  );
}

export default App;
